/**
 * Session-key transport encryption (RSA-OAEP + AES-256-GCM).
 *
 * Implements the contract in `frontend-session-key-implementation.md`:
 *   header  x-session-key: base64url(RSA-OAEP-SHA256(publicKey, <32 raw key bytes>))
 *   payload base64url(iv[12] || ciphertext || tag[16])
 *
 * The AES key is generated once per page load and held only in this module —
 * never Redux, never sessionStorage (the store persists there), never localStorage.
 *
 * Nothing here runs unless a URL is on `secureURLs` in baseQuery.ts.
 */

import { SECURE_URLS, TSecureEnvelope } from './encryptedEndpoints'

const IV_BYTES = 12
const KEY_BYTES = 32

/* ------------------------------------------------------------------ base64url */

const bytesToB64 = (bytes: Uint8Array): string => {
  let bin = ''
  const CHUNK = 0x8000 // chunked to avoid the argument limit on large payloads
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)))
  }
  return btoa(bin)
}

const b64ToBytes = (b64: string): Uint8Array => {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** base64url with `=` padding kept — the server uses urlsafe_b64decode, which requires it. */
const b64url = (bytes: Uint8Array): string =>
  bytesToB64(bytes).replace(/\+/g, '-').replace(/\//g, '_')

const unb64url = (str: string): Uint8Array => {
  const standard = str.replace(/-/g, '+').replace(/_/g, '/')
  const padded = standard + '='.repeat((4 - (standard.length % 4)) % 4)
  return b64ToBytes(padded)
}

/* ------------------------------------------------------------------ public key */

let _publicKey: CryptoKey | null = null

const getPublicKey = async (): Promise<CryptoKey> => {
  if (_publicKey) return _publicKey

  if (!window.crypto?.subtle) {
    throw new Error(
      '[secureTransport] crypto.subtle unavailable. Requires https:// or http://localhost — a LAN IP over plain http will not work.',
    )
  }

  const pem = process.env.REACT_APP_RSA_PUBLIC_KEY ?? ''
  if (!pem) {
    throw new Error(
      `[secureTransport] REACT_APP_RSA_PUBLIC_KEY is not set for env "${process.env.REACT_APP_ENV}". Each environment has its own keypair.`,
    )
  }

  // .env-cmdrc holds the PEM on one line with literal \n escapes
  const normalised = pem.replace(/\\n/g, '\n')
  const der = b64ToBytes(normalised.replace(/-----[^-]+-----|\s/g, ''))

  try {
    _publicKey = await window.crypto.subtle.importKey(
      'spki',
      der,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt'],
    )
  } catch (e) {
    throw new Error(
      '[secureTransport] Failed to import RSA public key. Must be SPKI ("-----BEGIN PUBLIC KEY-----"), not PKCS#1 ("-----BEGIN RSA PUBLIC KEY-----").',
    )
  }
  return _publicKey
}

/* ------------------------------------------------------------------ session */

type TSession = { aesKey: CryptoKey; header: string }

let _sessionPromise: Promise<TSession> | null = null

const createSession = async (): Promise<TSession> => {
  const raw = window.crypto.getRandomValues(new Uint8Array(KEY_BYTES))

  const aesKey = await window.crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ])

  // The RSA plaintext is the 32 RAW BYTES — not a base64 string of them.
  const wrapped = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    await getPublicKey(),
    raw,
  )

  return { aesKey, header: b64url(new Uint8Array(wrapped)) }
}

/**
 * One session key per page load.
 *
 * The *promise* is cached, not the result: baseQuery fires requests in parallel,
 * and caching only the result would let two concurrent callers each mint a key,
 * the second overwriting the first — so the first response would decrypt with the
 * wrong key. Caching the promise makes concurrent callers share one key.
 */
export const getSession = (): Promise<TSession> => {
  if (!_sessionPromise) _sessionPromise = createSession()
  return _sessionPromise
}

/** Value for the `x-session-key` header. Send it ONLY on migrated endpoints. */
export const getSessionKeyHeader = async (): Promise<string> => (await getSession()).header

/* ------------------------------------------------------------------ payload */

/** Encrypts an already-serialised JSON string, without re-stringifying it. */
export const encryptSecureString = async (plainText: string): Promise<string> => {
  const { aesKey } = await getSession()

  // Fresh IV on every call. Never hoist this out — reuse under one key breaks GCM.
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_BYTES))

  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    aesKey,
    new TextEncoder().encode(plainText),
  )

  const blob = new Uint8Array(IV_BYTES + ct.byteLength)
  blob.set(iv, 0)
  blob.set(new Uint8Array(ct), IV_BYTES)
  return b64url(blob)
}

export const encryptSecure = async (data: unknown): Promise<string> => {
  const { aesKey } = await getSession()

  // Fresh IV on every call. Never hoist this out — reuse under one key breaks GCM.
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_BYTES))

  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    aesKey,
    new TextEncoder().encode(JSON.stringify(data)),
  )

  const blob = new Uint8Array(IV_BYTES + ct.byteLength)
  blob.set(iv, 0)
  blob.set(new Uint8Array(ct), IV_BYTES)
  return b64url(blob)
}

/** Decrypts to the raw plaintext string, without parsing. */
export const decryptSecureToString = async (blob: string): Promise<string> => {
  const { aesKey } = await getSession()
  const raw = unb64url(blob)

  const pt = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: raw.subarray(0, IV_BYTES), tagLength: 128 },
    aesKey,
    raw.subarray(IV_BYTES),
  )
  return new TextDecoder().decode(pt)
}

/**
 * Decrypts and parses. Falls back to the raw string when the plaintext is not JSON —
 * /accounts/login returns a Python dict repr (single quotes, `None`), which the caller
 * fixes up before parsing. Mirrors decryptMessage's behaviour in Utils.
 */
export const decryptSecure = async (blob: string): Promise<any> => {
  const text = await decryptSecureToString(blob)
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}


/**
 * The session-key scheme is now the ONLY transport encryption — the legacy static
 * key was removed for VAPT #2, so there is no fallback to gate against.
 *
 * Every environment therefore needs its own REACT_APP_RSA_PUBLIC_KEY. Only `dev`
 * has one today; on an environment without a key, getPublicKey() throws a named
 * error at the first encrypted call rather than failing silently.
 */
export const isSecureEnabled = (): boolean => true

/** The envelope for this url, or undefined if it is not migrated / not enabled. */
export const getSecureEnvelope = (url: string): TSecureEnvelope | undefined =>
  isSecureEnabled() ? SECURE_URLS[url] : undefined

/**
 * Crypto failure vs auth failure, per backend §8 (there is no `error_code` field).
 *   400 + no encrypted field  → crypto failed
 *   401                       → session expired
 * Structural, so it does not depend on the wording of `error_text`.
 */
export const isCryptoFailure = (status?: number, body?: any): boolean =>
  status === 400 && !!body && !body.encrypted_data && !body.response_data

/** Test helper — forces a new session key on the next call. */
export const resetSession = (): void => {
  _sessionPromise = null
}
